import {
  FlatTreeNode,
  TreeNode,
  TreeNodeMap,
  TreeParentMap,
  TreeState,
} from '@/components/treeSelector/types/tree';
import { HydratedState } from '@/components/treeSelector/types/zustand';
import { CheckedState } from '@radix-ui/react-checkbox';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';

export type TreeStore<Type = unknown, Payload = unknown> = TreeState<Type, Payload> & {
  loadTree: (tree: TreeNode<Type, Payload>) => void;
  updateNodePayload: (nodeId: string, newPayload: Partial<Payload>) => void;
  toggleNode: (nodeId: string) => void;
  toggleExpansion: (nodeId: string) => void;
  search: (searchTerm: string) => void;
  updateAppliedNodes: (
    checkedMap: Record<string, CheckedState>,
    nodeMap: TreeNodeMap<Type, Payload>,
  ) => void;
  saveStateToSession: () => void;
} & HydratedState;

export const createTreeStore = <Type = unknown, Payload = unknown>(
  initialState: TreeState<Type, Payload>,
  typeHierarchy: readonly Type[],
  {
    persistKey,
    partialize,
  }: {
    persistKey: string;
    partialize?: (state: TreeStore<Type, Payload>) => Partial<TreeState<Type, Payload>>;
  },
) =>
  create<TreeStore<Type, Payload>>()(
    persist(
      devtools(
        (set, get) => ({
          ...initialState,
          toggleExpansion: (nodeId: string) => {
            const { expandedNodes, visibleTree, searchTerm } = get();
            const isExpanded = expandedNodes.includes(nodeId);
            const newExpanded = isExpanded
              ? expandedNodes.filter((node) => node !== nodeId)
              : [...expandedNodes, nodeId];
            const isInSearchMode = searchTerm !== '';
            const expandedNodesPreSearch = isInSearchMode
              ? get().expandedNodesPreSearch
              : newExpanded;
            set(
              {
                expandedNodes: newExpanded,
                expandedNodesPreSearch,
                flattenedTree: flattenTree(visibleTree, newExpanded, typeHierarchy),
              },
              false,
              'toggleExpansion',
            );
          },
          loadTree: (tree) => {
            const { parentMap, nodeMap } = buildLookupMaps(tree);
            const newState = {
              tree,
              visibleTree: tree,
              checked: {},
              parentMap,
              nodeMap,
              visibleNodeMap: nodeMap,
              searchTerm: get().searchTerm,
              expandedNodes: [],
              expandedNodesPreSearch: [],
              flattenedTree: [],
              appliedNodes: Object.keys(nodeMap).filter(
                (node) => nodeMap[node].type === typeHierarchy[typeHierarchy.length - 1],
              ),
              hasLoadedTree: true,
            };
            const filteredState = filterNodes(newState, get().searchTerm);
            const expandedNodes = filteredState.visibleTree.children.map((c) => c.id);
            const flattenedTree = flattenTree(
              filteredState.visibleTree,
              expandedNodes,
              typeHierarchy,
            );
            set(
              {
                ...filteredState,
                flattenedTree,
                expandedNodes,
                expandedNodesPreSearch: expandedNodes,
              },
              false,
              'loadTree',
            );
          },
          updateNodePayload: (nodeId: string, newPayload: Partial<Payload>) => {
            const state = get();

            mutateTreeStateWithUpdatedPayload(state.tree, nodeId, newPayload);
            set({ ...state }, false, 'updateNodePayload');
          },
          search: (searchTerm) => {
            const strippedSearchTerm = searchTerm.trim();
            const state = get();
            const startedNewSearch = strippedSearchTerm !== '' && state.searchTerm === '';
            const filteredState = filterNodes(state, strippedSearchTerm);
            const expandedNodesPreSearch = startedNewSearch
              ? state.expandedNodes
              : state.expandedNodesPreSearch;
            const expandedNodes =
              strippedSearchTerm !== ''
                ? Object.keys(filteredState.visibleNodeMap)
                : expandedNodesPreSearch;
            const flattenedTree = flattenTree(
              filteredState.visibleTree,
              expandedNodes,
              typeHierarchy,
            );
            set(
              { ...filteredState, expandedNodes, expandedNodesPreSearch, flattenedTree },
              false,
              'search',
            );
          },
          toggleNode: (nodeId) => {
            const state = get();
            const newState = toggleNode(state, nodeId);
            set(newState, false, 'toggleNode');
          },
          // TODO: This should probably just use checkedmap and nodemap from get()?
          updateAppliedNodes: (checkedMap, nodeMap) => {
            const checkedNodes = Object.keys(checkedMap).filter((id) => checkedMap[id] === true);
            if (checkedNodes.length === 0) {
              set(
                {
                  appliedNodes: Object.keys(nodeMap).filter(
                    (node) => nodeMap[node].type === typeHierarchy[typeHierarchy.length - 1],
                  ),
                },
                false,
                'updateAppliedNodesToDefault',
              );
              return;
            }

            const appliedNodeIds: string[] = [];

            checkedNodes.forEach((id) => {
              const node = nodeMap[id];

              if (node && node.type === typeHierarchy[typeHierarchy.length - 1]) {
                appliedNodeIds.push(node.id);
              }
            });

            set({ appliedNodes: appliedNodeIds }, false, 'updateAppliedNodes');
          },
          saveStateToSession: () => {
            const state = get();
            const persistedState = partialize ? partialize(state) : state;
            sessionStorage.setItem(persistKey, JSON.stringify({ state: persistedState }));
          },
          _hasHydrated: false,
          setHasHydrated: (state) => {
            set({ _hasHydrated: state }, false, 'setHasHydrated');
          },
        }),
        { name: 'treeStore' },
      ),
      {
        name: persistKey,
        storage: createJSONStorage(() => sessionStorage),
        onRehydrateStorage: (state) => {
          return () => state.setHasHydrated(true);
        },
        ...(partialize ? { partialize } : {}),
      },
    ),
  );

const buildLookupMaps = <Type = unknown, Payload = unknown>(
  tree: TreeNode<Type, Payload>,
): { parentMap: TreeParentMap; nodeMap: TreeNodeMap<Type, Payload> } => {
  const parentMap: TreeParentMap = {};
  const nodeMap: TreeNodeMap<Type, Payload> = {};

  const traverse = (node: TreeNode<Type, Payload>, parentId?: string): void => {
    nodeMap[node.id] = node;
    if (parentId) {
      parentMap[node.id] = parentId;
    }
    node.children?.forEach((child) => traverse(child, node.id));
  };

  traverse(tree);
  return { parentMap, nodeMap };
};

const filterNodes = <Type = unknown, Payload = unknown>(
  state: TreeState<Type, Payload>,
  searchTerm: string,
) => {
  if (searchTerm === '') {
    return { ...state, searchTerm, visibleTree: state.tree, visibleNodeMap: state.nodeMap };
  }
  const searchMap = {};
  const visibleTreeNodes: Record<string, boolean> = {};
  markSearchHits(state.tree, searchTerm, searchMap);
  for (const searchHit of Object.keys(searchMap)) {
    const node = state.nodeMap[searchHit];
    visibleTreeNodes[node.id] = true;
    markAllDescendantsAsVisible(node, visibleTreeNodes);
    markParentsAsVisible(state, node, visibleTreeNodes);
  }
  const visibleTree = constructVisibleTree(state.tree, visibleTreeNodes);
  const { nodeMap: visibleNodeMap } = buildLookupMaps(visibleTree);
  return { ...state, visibleTree, visibleNodeMap, searchTerm };
};

const markSearchHits = <Type = unknown, Payload = unknown>(
  node: TreeNode<Type, Payload>,
  searchTerm: string,
  searchMap: Record<string, boolean>,
) => {
  node.children.forEach((child) => {
    const matchesSearch = child.label?.toLowerCase().includes(searchTerm.toLowerCase());
    if (matchesSearch) {
      searchMap[child.id] = true;
    }
    markSearchHits(child, searchTerm, searchMap);
  });
};

const markAllDescendantsAsVisible = (node: TreeNode, visibleTreeNodes: Record<string, boolean>) => {
  node.children.forEach((child) => {
    visibleTreeNodes[child.id] = true;
    markAllDescendantsAsVisible(child, visibleTreeNodes);
  });
};

const markParentsAsVisible = (
  state: TreeState,
  node: TreeNode,
  visibleTreeNodes: Record<string, boolean>,
) => {
  const { parentMap, nodeMap } = state;
  const parent = parentMap[node.id];
  if (parent === undefined) return;
  const isAlreadyVisible = visibleTreeNodes[parent];
  if (isAlreadyVisible) return; // All parents should be visible then
  const parentNode = nodeMap[parent];
  visibleTreeNodes[parentNode.id] = true;
  markParentsAsVisible(state, parentNode, visibleTreeNodes);
};

const constructVisibleTree = <Type = unknown, Payload = unknown>(
  node: TreeNode<Type, Payload>,
  visibleTreeNodes: Record<string, boolean>,
): TreeNode<Type, Payload> => {
  if (!node.children) return node;
  return {
    ...node,
    children: node.children
      .filter((child) => visibleTreeNodes[child.id])
      .map((child) => constructVisibleTree(child, visibleTreeNodes)),
  };
};

const flattenTree = <Type = unknown, Payload = unknown>(
  tree: TreeNode<Type, Payload>,
  expandedNodes: string[],
  typeHierarchy: readonly Type[],
): FlatTreeNode<Type, Payload>[] => {
  const result: FlatTreeNode<Type, Payload>[] = [];

  const traverse = (node: TreeNode<Type, Payload>, level: number, type: Type) => {
    const isExpanded = expandedNodes.includes(node.id);

    result.push({ id: node.id, node, level, type, isExpanded });

    if (isExpanded && node.children.length > 0) {
      const childType = typeHierarchy[typeHierarchy.indexOf(node.type) + 1];
      node.children.forEach((child) => {
        traverse(child, level + 1, childType);
      });
    }
  };

  if (tree.children) {
    tree.children.forEach((child) => {
      const firstTypeAfterRoot = typeHierarchy[1];
      traverse(child, 0, firstTypeAfterRoot);
    });
  }

  return result;
};

const toggleNode = <Type = unknown, Payload = unknown>(
  state: TreeState<Type, Payload>,
  nodeId: string,
) => {
  const visibleNode = state.visibleNodeMap[nodeId];
  const originalNode = state.nodeMap[nodeId];
  if (!visibleNode || !originalNode) {
    console.error('Tried checking node that could not be found', state, nodeId);
    return state;
  }
  const oldNodeState = state.checked[visibleNode.id];
  const newNodeState = oldNodeState === 'indeterminate' || oldNodeState === true ? false : true;

  const shouldCheckNodeWhileFiltersAreApplied =
    newNodeState === true &&
    visibleNode.children.length !== 0 &&
    Object.keys(state.nodeMap).length !== Object.keys(state.visibleNodeMap).length;

  if (shouldCheckNodeWhileFiltersAreApplied) {
    checkVisibleNodeAndVisibleChildren(state, visibleNode);
  } else {
    state.checked[visibleNode.id] = newNodeState;
    setChildrenCheckedState(state, visibleNode, newNodeState);
  }

  updateParentState(state, visibleNode);

  return { ...state, checked: { ...state.checked } };
};

const checkVisibleNodeAndVisibleChildren = <Type = unknown, Payload = unknown>(
  state: TreeState<Type, Payload>,
  node: TreeNode<Type, Payload>,
) => {
  if (node.children.length === 0) {
    state.checked[node.id] = true;
    return;
  }
  node.children.forEach((c) => checkVisibleNodeAndVisibleChildren(state, c));
  const originalNode = state.nodeMap[node.id];
  const newCheckedState = originalNode.children.every((c) => state.checked[c.id] === true)
    ? true
    : 'indeterminate';
  state.checked[node.id] = newCheckedState;
};

const setChildrenCheckedState = (state: TreeState, node: TreeNode, checked: CheckedState) => {
  for (const child of node.children) {
    state.checked[child.id] = checked;
    setChildrenCheckedState(state, child, checked);
  }
};

const updateParentState = <Type = unknown, Payload = unknown>(
  state: TreeState<Type, Payload>,
  node: TreeNode<Type, Payload>,
): void => {
  const { parentMap, nodeMap } = state;
  const parent = parentMap[node.id];
  const parentIsRoot = parentMap[parent] === undefined;
  if (parentIsRoot) {
    // The root node operates differently from all children. It's the "check all" button.
    if (state.checked[node.id] === false) state.checked[parent] = false;
    return;
  }
  const parentNode = nodeMap[parent];
  if (!parentNode) {
    console.error('Could not find parent node in node map even though it exists in parent map');
    return;
  }

  const siblings = parentNode.children;
  const siblingsCounts = siblings.reduce(
    (acc, curr) => {
      if (state.checked[curr.id] === true) {
        acc.checked += 1;
      } else if (state.checked[curr.id] === 'indeterminate') {
        acc.indeterminate += 1;
      }
      return acc;
    },
    { checked: 0, indeterminate: 0 },
  );

  const shouldUncheckParent = siblingsCounts.checked === 0 && siblingsCounts.indeterminate === 0;
  const shouldCheckParent = siblingsCounts.checked === siblings.length;
  const parentState = shouldUncheckParent ? false : shouldCheckParent ? true : 'indeterminate';
  state.checked[parentNode.id] = parentState;

  return updateParentState(state, parentNode);
};

const mutateTreeStateWithUpdatedPayload = <Type, Payload>(
  treeNode: TreeNode<Type, Payload>,
  nodeId: string,
  newPayload: Partial<Payload>,
) => {
  if (treeNode.payload && treeNode.id === nodeId)
    return (treeNode.payload = { ...treeNode.payload, ...newPayload });

  for (const child of treeNode.children) {
    mutateTreeStateWithUpdatedPayload(child, nodeId, newPayload);
  }
  return;
};
